import { useEffect, useId, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Fab, Tooltip, Zoom, useMediaQuery } from "@mui/material";
import { AutoAwesome, Chat } from "@mui/icons-material";
import useAiEligibility from "src/_modules/AiSupportChat/hooks/useAiEligibility";
import {
  AiChatProvider,
  useAiChatContext,
} from "src/_modules/AiSupportChat/context/AiChatProvider";
import AiChatPanel from "src/_modules/AiSupportChat/components/AiChatPanel";
import PanelShell from "src/_modules/AiSupportChat/components/PanelShell";
import {
  LAUNCHER_INSET_BOTTOM,
  LAUNCHER_INSET_END,
  LAUNCHER_SIZE,
  REDUCED_MOTION_QUERY,
  Z_INDEX,
} from "src/_modules/AiSupportChat/constants";

/** The admin layout is the only one without the Help Center tab. */
const ADMIN_PATH = /^\/admin(\/|$)/;

/**
 * The Fab + panel. `hidden` (outside the dashboards) renders neither but
 * keeps the provider above, so a live answer and the open panel survive.
 *
 * @param {{ hidden: boolean }} props
 */
const Launcher = ({ hidden }) => {
  const { t, i18n } = useTranslation("aiSupport");
  const { pathname } = useLocation();
  const tourRunning = useSelector((state) => state.entities.tutorial.run);
  const { panel } = useAiChatContext();
  const open = panel.isOpen;
  const titleId = useId();
  const panelId = useId();
  const fabRef = useRef(null);
  const wasOpen = useRef(false);
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });

  // Closing hands focus back to the launcher
  useEffect(() => {
    if (wasOpen.current && !open) fabRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // The Fab stays under the floating panel and toggles it (shell A)
  const showFab = !tourRunning;
  const insetEnd = ADMIN_PATH.test(pathname)
    ? LAUNCHER_INSET_END.default
    : LAUNCHER_INSET_END.withHelpTab;
  const label = t(open ? "launcher.close" : "launcher.open");

  if (hidden) return null;
  return (
    <>
      {/* Zoom keeps the Fab mounted (hidden) so focus can return to it */}
      <Zoom in={showFab} timeout={reduceMotion ? 0 : undefined}>
        <Box
          // `<html>` has no `lang`: the label is read in the UI language
          lang={i18n.language}
          sx={{
            position: "fixed",
            bottom: LAUNCHER_INSET_BOTTOM,
            insetInlineEnd: insetEnd,
            zIndex: Z_INDEX.launcher,
          }}
        >
          {/* An empty title keeps a hover tooltip from outliving the Fab */}
          <Tooltip
            title={showFab ? (open ? label : t("launcher.tooltip")) : ""}
            placement="top"
          >
            <Fab
              ref={fabRef}
              color="primary"
              aria-label={label}
              aria-expanded={open}
              // The panel is mounted only while open
              aria-controls={open ? panelId : undefined}
              onClick={panel.toggle}
              sx={{ width: LAUNCHER_SIZE, height: LAUNCHER_SIZE }}
            >
              {open ? <Chat /> : <AutoAwesome />}
            </Fab>
          </Tooltip>
        </Box>
      </Zoom>
      <PanelShell
        open={open}
        onClose={panel.close}
        labelledBy={titleId}
        id={panelId}
        insetInlineEnd={insetEnd}
      >
        <AiChatPanel titleId={titleId} />
      </PanelShell>
    </>
  );
};

/**
 * Self-gating entry point of the AI support chat, mounted once in App.js.
 * Renders nothing unless the pre-gate and `/eligibility` pass (§3.1), so it
 * can sit outside the role layouts and survive route changes. Outside
 * `/merchant` and `/admin` the chat stays mounted but hidden (`mounted`
 * without `visible`), so a short trip away doesn't abort an answer. The
 * provider is keyed by profile id: another user never inherits this one's
 * chat state or open conversation.
 */
export default function AiChatLauncher() {
  const { visible, mounted } = useAiEligibility();
  const profileId = useSelector((state) => state.entities.profile.profile?.id);

  if (!mounted) return null;
  return (
    <AiChatProvider key={profileId} profileId={profileId}>
      <Launcher hidden={!visible} />
    </AiChatProvider>
  );
}
